import React, { useEffect } from 'react';
import { Bot, Cog, FileText, RefreshCw, BarChart3, MessageSquare, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';

const AIAutomationPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  return (
    <div className="min-h-screen bg-white">
    <Seo title="AI Automation for Logistics & eCommerce" description="Agentic AI, document processing, and workflow automation for supply chain operations. Turn manual EDI, invoice, and shipping-doc workflows into automated pipelines." path="/services/automation" />
     <Header />

      {/* Services Grid */}
      <section className="py-20 px-4 bg-white text-black">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">AI Automation for Logistics &amp; eCommerce</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From intelligent document processing to fully autonomous AI agents,
            we deliver automation solutions across the entire spectrum.
          </p>
          <br />
          <br />
          <div className=" bg-black text-white rounded-xl grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-left">
            {[
              {
                icon: <Bot size={24} />,
                title: "Agentic AI",
                desc: "Autonomous AI agents that reason, plan, and execute multi-step business processes. From customer inquiry handling to supply chain decision-making, our agents work alongside your team.",
                features: [
                  "Multi-step task orchestration",
                  "Tool-use & API integration",
                  "Human-in-the-loop workflows",
                  "Context-aware decision making",
                ],
              },
              {
                icon: <Cog size={24} />,
                title: "Robotic Process Automation",
                desc: "Automate repetitive, rule-based tasks across your existing systems without costly re-platforming. RPA bots work with your current tools to eliminate manual data entry and transfers.",
                features: [
                  "Screen scraping & data extraction",
                  "Cross-system data transfer",
                  "Scheduled & triggered automation",
                  "Legacy system integration",
                ],
              },
              {
                icon: <FileText size={24} />,
                title: "Document Processing",
                desc: "AI-powered extraction and processing of invoices, purchase orders, shipping documents, and more. Turn unstructured documents into structured, actionable data automatically.",
                features: [
                  "Invoice & PO processing",
                  "BOL & shipping doc extraction",
                  "Email parsing & classification",
                  "Data validation & enrichment",
                ],
              },
              {
                icon: <RefreshCw size={24} />,
                title: "Workflow Orchestration",
                desc: "Design and implement end-to-end automated workflows that connect your people, processes, and systems. Replace manual handoffs with intelligent routing and automated escalations.",
                features: [
                  "Business process automation",
                  "Approval routing & escalation",
                  "Event-driven workflows",
                  "Cross-department coordination",
                ],
              },
              {
                icon: <BarChart3 size={24} />,
                title: "Predictive Analytics",
                desc: "Leverage machine learning to forecast demand, detect anomalies, and optimize operations. Turn your historical data into forward-looking intelligence that drives better decisions.",
                features: [
                  "Demand forecasting",
                  "Anomaly detection & alerting",
                  "Inventory optimization",
                  "Performance prediction",
                ],
              },
              {
                icon: <MessageSquare size={24} />,
                title: "Conversational AI",
                desc: "Deploy intelligent chatbots and virtual assistants that handle customer inquiries, qualify leads, and provide 24/7 support — integrated directly with your existing systems.",
                features: [
                  "Customer support automation",
                  "Lead qualification bots",
                  "Internal helpdesk assistants",
                  "Multi-channel deployment",
                ],
              },
            ].map((service, i) => (
              <div
                key={i}
                className="bg-white/5 p-8 hover:bg-white/10 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-black text-white flex items-center justify-center mb-6 transition-transform group-hover:scale-110">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  {service.desc}
                </p>
                <ul className="space-y-2">
                  {service.features.map((f, j) => (
                    <li
                      key={j}
                      className="text-sm text-gray-300 flex items-start"
                    >
                      <span className="mr-2 mt-1 w-1 h-1 bg-white rounded-full flex-shrink-0"></span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-black mb-6">
              Your team spends too much time on manual processes.
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Every hour your team spends on repetitive data entry, manual
              document processing, or copy-pasting between systems is an hour
              not spent on growth. We work with you to understand your pain
              points, map your workflows, and design automation solutions that
              free your team to focus on what matters.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            {[
              {
                stat: "57%",
                label: "of U.S. work hours are automatable with current technology",
                source: "McKinsey Global Institute, 2025",
              },
              {
                stat: "90%",
                label: "reduction in manual data entry time with RPA",
                source: "Deloitte",
              },
              {
                stat: "75%",
                label: "decrease in manual processing errors",
                source: "Forrester Research, 2024",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white p-8 text-center shadow-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105 cursor-pointer"
              >
                <div className="text-5xl text-indigo-600 font-bold mb-3">{item.stat}</div>
                <p className="text-gray-600 mb-2">{item.label}</p>
                <p className="text-xs text-gray-400 font-mono">{item.source}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gray-100">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-8">
            Let's automate what's slowing you down.
          </h2>
          <p className="text-xl text-gray-900 mb-12 leading-relaxed">
            Tell us what your team still does by hand — the copy-paste between systems,
            the invoices someone keys in, the emails that get sorted one at a time.
            We'll tell you what can be automated and what it's worth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://calendly.com/dan-tarelabs/new-meeting"
              className="bg-black hover:bg-gray-800 text-white px-10 py-5 font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl inline-flex items-center justify-center group"
            >
              Kill the Manual Work
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="mailto:dan@tarelabs.com"
              className="border-2 border-black text-black px-10 py-5 font-bold text-lg transition-all duration-300 hover:bg-black hover:text-white inline-flex items-center justify-center"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>
     <Footer />
    </div>
  );
};

export default AIAutomationPage;
